import { h } from '../lib/dom.js';
import { money, pct } from '../lib/format.js';
import { listScenarios, deleteScenario } from '../lib/storage.js';

export function ScenariosPage() {
  const root = h('div', { class: 'max-w-[1200px] mx-auto px-6 md:px-10' });
  render();
  return root;

  function render() {
    const scenarios = listScenarios().sort(
      (a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''),
    );
    const totalProfit = scenarios.reduce((sum, s) => sum + (s.outputs?.forecast_profit ?? 0), 0);

    root.innerHTML = '';

    // Header
    root.appendChild(h('header', { class: 'pt-10 pb-6 flex items-end justify-between gap-6 flex-wrap border-b border-[color:var(--color-rule)]' },
      h('div', null,
        h('div', { class: 'folio' }, `Ledger · ${scenarios.length} entr${scenarios.length === 1 ? 'y' : 'ies'}`),
        h('h1', { class: 'display display-md mt-1' }, 'Every feasibility, captured.'),
      ),
      h('a', { href: '#/feaso', class: 'btn btn-ink' }, 'New feasibility'),
    ));

    if (scenarios.length === 0) {
      root.appendChild(emptyState());
      return;
    }

    // Summary strip
    root.appendChild(h('div', { class: 'py-6 grid grid-cols-2 md:grid-cols-4 gap-6 border-b border-[color:var(--color-rule)]' },
      stat('Scenarios', String(scenarios.length)),
      stat('Aggregate profit', money(totalProfit)),
      stat('Best %', pct(maxBy(scenarios, (s) => s.outputs?.profit_on_cost_pct ?? 0))),
      stat('Last updated', formatDate(scenarios[0]?.updated_at)),
    ));

    // Ledger
    root.appendChild(h('ol', { class: 'divide-y divide-[color:var(--color-rule)]' },
      ...scenarios.map((s, i) => h('li', null, row(s, i + 1, render))),
    ));
  }
}

function row(s, n, onChange) {
  const poc = s.outputs?.profit_on_cost_pct ?? 0;
  const profit = s.outputs?.forecast_profit ?? 0;
  const signal =
    poc >= 0.15 ? 'text-[color:var(--color-positive)]'
    : poc >= 0.10 ? 'text-[color:var(--color-warning)]'
    : 'text-[color:var(--color-negative)]';

  return h('div', { class: 'group grid grid-cols-12 gap-4 items-baseline py-6' },
    h('div', { class: 'col-span-1 folio num' }, String(n).padStart(2, '0')),
    h('a', { href: `#/feaso/${s.id}`, class: 'col-span-4 min-w-0' },
      h('div', { class: 'display display-sm truncate' }, s.name || s.inputs?.address || 'Untitled'),
      s.inputs?.address && s.inputs.address !== s.name
        ? h('div', { class: 'text-sm text-[color:var(--color-ink-dim)] truncate' }, s.inputs.address)
        : null,
    ),
    h('div', { class: 'col-span-2 num text-[color:var(--color-ink-soft)] text-sm' },
      h('div', null, `${s.inputs?.dwellings ?? '—'} dwelling${s.inputs?.dwellings === 1 ? '' : 's'}`),
      h('div', { class: 'text-xs text-[color:var(--color-ink-faint)]' }, money(s.inputs?.purchase_price ?? 0)),
    ),
    h('div', { class: `col-span-2 num display display-sm text-right ${signal}` }, money(profit)),
    h('div', { class: `col-span-2 text-right num ${signal}` }, pct(poc)),
    h('div', { class: 'col-span-1 text-right' },
      h('button', {
        type: 'button',
        class: 'text-[color:var(--color-ink-faint)] hover:text-[color:var(--color-negative)] text-xs opacity-0 group-hover:opacity-100 transition-opacity',
        onclick: () => {
          if (confirm(`Delete "${s.name}"?`)) {
            deleteScenario(s.id);
            onChange();
          }
        },
      }, 'Remove'),
    ),
  );
}

function stat(label, value) {
  return h('div', null,
    h('div', { class: 'folio' }, label),
    h('div', { class: 'display display-sm num mt-1' }, value),
  );
}

function emptyState() {
  return h('div', { class: 'py-16 md:py-24 grid md:grid-cols-12 gap-10' },
    h('div', { class: 'md:col-span-7' },
      h('div', { class: 'folio mb-3' }, 'Empty ledger'),
      h('h2', { class: 'display display-md max-w-[22ch]' }, 'Price your first deal to begin the record.'),
      h('p', { class: 'mt-4 text-[color:var(--color-ink-soft)] max-w-[52ch]' },
        'Saved feasibilities land here as a running ledger — with the deal, the profit, and the profit on cost. Sort, compare, return when a seller moves.',
      ),
      h('a', { href: '#/feaso', class: 'btn btn-ink mt-6' }, 'Begin →'),
    ),
    h('aside', { class: 'md:col-span-5 md:pl-8 md:border-l border-[color:var(--color-rule)] text-sm text-[color:var(--color-ink-dim)]' },
      h('div', { class: 'folio mb-3 text-[color:var(--color-ink-soft)]' }, 'What you\u2019ll see here'),
      h('ul', { class: 'space-y-3 list-none' },
        h('li', null, '· Name, address, and dwelling count.'),
        h('li', null, '· Forecast profit and profit on cost, colour-signalled against your 15% threshold.'),
        h('li', null, '· A one-click return to any scenario with all inputs and overrides restored.'),
      ),
    ),
  );
}

function maxBy(arr, fn) {
  let best = 0;
  for (const x of arr) {
    const v = fn(x);
    if (v > best) best = v;
  }
  return best;
}

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); }
  catch { return '—'; }
}
